import {BarChart, DonutChart} from '@mantine/charts'
import {Box, Card, Skeleton, Title} from '@mantine/core'
import {useMemo} from 'react'
import {useProjects} from '../../providers/projects/useProjects.ts'
import {mapBarChart} from './mapper/mapBarChart.ts'
import {mapDonutChart} from './mapper/mapDonutChart.ts'
import {api} from '../../trpc/api.ts'
import {ProjectBadge} from '../calendar/components/badges/ProjectBadge.tsx'
import {mapHighlights} from './mapper/mapHighlights.ts'
import {useFilters} from './providers/filters/useFilters.ts'
import {ProjectSelect} from '../calendar/components/ProjectSelect.tsx'
import {FilterBar} from '../../components/filterBar/FilterBar.tsx'
import {DateRangeSelector} from '../../components/dateRangeSelector/DateRangeSelector.tsx'
import {HoursByProjectTable} from './HoursByProjectTable.tsx'
import {formatLocalDate} from '../calendar/formatter/formatLocalDate.ts'
import {formatHours} from '../calendar/formatter/formatHours.ts'
import {useUserSettings} from '../../providers/userSettings/useUserSettings.ts'
import type {ProjectId} from '@package/shared'

export const DashboardContainer = () => {
  const {userSettings} = useUserSettings()
  const {month, setMonth, projectId, setProjectId} = useFilters()
  const {projects, loading: loadingProjects} = useProjects()

  const {data: dayHoursByProject, isLoading: loadingDayHoursByProject} = api.aggregations.dayHoursByProject.useQuery({
    start: month.start.toISOString(),
    end: month.end.toISOString(),
  })

  const filteredDayHoursByProject = useMemo(() => {
    return !projectId //
      ? dayHoursByProject || []
      : (dayHoursByProject || []).filter((item) => item.projectId === projectId)
  }, [dayHoursByProject, projectId])

  const barChartData = useMemo(
    () => mapBarChart(month.start, month.end, filteredDayHoursByProject, projects),
    [filteredDayHoursByProject, month, projects],
  )
  const donutChartData = useMemo(
    () => mapDonutChart(filteredDayHoursByProject, projects),
    [filteredDayHoursByProject, projects],
  )
  const {totalDays, totalHours} = useMemo(() => mapHighlights(filteredDayHoursByProject), [filteredDayHoursByProject])

  const loading = loadingProjects || loadingDayHoursByProject
  const disabled = loading

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 15}}>
      <div style={{display: 'flex', gap: 15}}>
        <FilterBar
          mainControls={
            <DateRangeSelector range={'month'} value={month} onValueChange={setMonth} disabled={disabled} />
          }
          secondaryControls={
            <ProjectSelect value={projectId} onChange={setProjectId} allowArchivedSelection disabled={disabled} />
          }
        />
      </div>
      <div style={{display: 'flex', width: '100%', gap: 15, flexWrap: 'wrap'}}>
        <div style={{flex: 1, minWidth: 200, maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 15}}>
          <Card style={{flex: 1, alignItems: 'center', justifyContent: 'center'}}>
            <Title order={4}>Total days</Title>
            {loading && <Skeleton visible={loading} h={35} w={70} />}
            {!loading && <Title order={2}>{totalDays || 0}</Title>}
          </Card>
          <Card style={{flex: 1, alignItems: 'center', justifyContent: 'center'}}>
            <Title order={4}>Total hours</Title>
            {loading && <Skeleton visible={loading} h={35} w={70} />}
            {!loading && <Title order={2}>{formatHours(totalHours)}</Title>}
          </Card>
        </div>

        <Card
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 25,
            flexWrap: 'wrap',
            minWidth: 300,
          }}
        >
          {donutChartData.data.length === 0 && (
            <Box p={40}>
              <DonutChart
                size={200}
                thickness={30}
                withLabels={false}
                withTooltip={false}
                data={[{name: '', value: 1, color: 'dimmed'}]}
                tooltipDataSource={'segment'}
              />
            </Box>
          )}
          {donutChartData.data.length > 0 && (
            <DonutChart
              {...donutChartData}
              size={200}
              thickness={30}
              chartLabel={formatHours(totalHours ?? 0)}
              withLabels
              withTooltip
              tooltipDataSource={'segment'}
            />
          )}
          <div style={{minHeight: 200}}>
            <HoursByProjectTable
              data={donutChartData.data.map((item) => ({projectId: item.key, hours: item.value}))}
              loading={loading}
            />
          </div>
        </Card>
      </div>
      <Card pt={'xl'}>
        <BarChart
          {...barChartData}
          h={400}
          type={'stacked'}
          tickLine={'x'}
          yAxisLabel={'Hours'}
          yAxisProps={{minTickGap: 1, width: 60}}
          valueFormatter={formatHours}
          tooltipProps={{
            content: ({payload = [], ...rest}) => {
              if (typeof rest.label !== 'number') return null
              return (
                <Card withBorder>
                  <Title order={5}>{formatLocalDate(month.start.set('date', rest.label), userSettings, true)}</Title>
                  <HoursByProjectTable
                    data={payload.map(({name, value}) => ({projectId: name as ProjectId, hours: value as number}))}
                    loading={false}
                  />
                </Card>
              )
            },
          }}
        />
        <div style={{alignSelf: 'end', display: 'flex', gap: 5, flexWrap: 'wrap'}}>
          {barChartData.series.map((series) => (
            <ProjectBadge key={series.name} projectId={series.name} />
          ))}
        </div>
      </Card>
    </div>
  )
}
